import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import type SupplierItemModel from './supplier-item';
import type CompanyModel from './company';
import type UserModel from './user';
import type ProductGroupModel from './product-group';
import type LocationItemModel from './location-item';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { displaySize } from 'bcl/helpers/display-size';
const formatter = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 2,
});
export default class ItemModel extends Model {
  declare [Type]: 'item';

  @attr()
  declare name: string;
  @attr()
  declare short_name: string;
  @attr()
  declare active: boolean;
  @belongsTo('product-group', { async: false, inverse: null })
  declare product_group: ProductGroupModel;
  declare orig_product_group: ProductGroupModel | undefined;
  @action
  updateProductGroup(pg: ProductGroupModel) {
    if (!this.orig_product_group) {
      this.orig_product_group = this.product_group;
    }
    this.product_group = pg;
  }
  @attr('number')
  declare deposit_container: number;
  @attr('number')
  declare deposit_single: number;
  @attr('number')
  declare gross_price: number; // einkaufspreis per gebinde
  @attr('number')
  declare container_size: number; // flaschen per gebinde
  @attr('number')
  declare single_size: number; // ml per flasche
  @attr('number')
  declare target_amount: number; // sollbestand global
  get displayTargetAmount() {
    return formatter.format(
      (this.target_amount ?? 0) / (this.container_size ?? 1)
    );
  }
  get virtual_target_amount() {
    return Math.floor((this.target_amount ?? 0) / (this.container_size ?? 1));
  }
  set virtual_target_amount(val) {
    this.target_amount = val * (this.container_size ?? 1);
  }
  get gebinde() {
    return htmlSafe(
      `${this.container_size} x ${displaySize([this.single_size])}`
    );
  }
  @attr('number')
  declare serving_size: number; // ausschankgröße in ml
  @belongsTo('company', { async: false, inverse: 'items' })
  declare company: CompanyModel;
  @attr()
  declare created_at: string;
  @attr()
  declare updated_at: string;
  @belongsTo('user', { async: false, inverse: null })
  declare created_by: UserModel;
  @belongsTo('user', { async: false, inverse: null })
  declare updated_by: UserModel;

  @hasMany('supplier-item', { async: false, inverse: 'item' })
  declare supplierItems: SupplierItemModel[];
  @hasMany('location-item', { async: false, inverse: 'item' })
  declare locationItems: LocationItemModel[];
  get locationAmounts() {
    const obj = {} as StringHash;
    // fake order for all
    obj[0] = 0;
    if (!this.locationItems.length) {
      return {};
    }
    this.locationItems.forEach((locationItem) => {
      const crates = locationItem.rounded_calculated_amount;
      obj[locationItem.location.id] = crates;
      obj[0] += crates;
    });
    return obj;
  }
  get orderedAndNotArrivedAmount() {
    let amount = 0;
    this.supplierItems.forEach((suplItem) => {
      suplItem.orderEntries.forEach((ordEntry) => {
        if (ordEntry.order?.status === 'ordered') {
          if (ordEntry.order?.accepted_date === null) {
            amount += ordEntry.amount;
          }
        }
      });
    });
    return amount;
  }
  get orderedAmountAndDate() {
    const obj = [];
    this.supplierItems.forEach((suplItem) => {
      suplItem.orderEntries.forEach((ordEntry) => {
        if (ordEntry.order?.status === 'ordered') {
          if (ordEntry.order?.accepted_date === null) {
            obj.push([
              ordEntry.order.delivery_date,
              ordEntry.amount,
              ordEntry.order.id,
            ]);
          }
        }
      });
    });
    return obj;
  }
  get firstArticleNr() {
    const supplierItems = this.supplierItems ?? [];
    for (let i = 0; i < supplierItems.length; i++) {
      const si = supplierItems[i];
      if (si.articlenr) {
        return si.articlenr;
      }
    }
    return '';
  }
  get shouldBeSaved() {
    if (this.hasDirtyAttributes) {
      return true;
    } else {
      if (this.product_group != this.orig_product_group) {
        return true;
      } else {
        return false;
      }
    }
  }
  @action rollback() {
    this.rollbackAttributes();
    if (!this.isDeleted && this.orig_product_group) {
      this.product_group = this.orig_product_group;
    }
  }
}
interface StringHash {
  [index: string]: number[];
}
